import type { Firestore } from 'firebase/firestore';
import type { Firestore as RestFirestore } from 'firebase/firestore/lite';
import type { WireFrame, WireMap } from './outputViewCodec';
import { createOutputViewKey, type OutputViewEndReason, type OutputViewKey } from './outputViewPeer';
import { OutputViewStream, type OutputViewMapState, type OutputViewWireReport } from './outputViewStream';
import {
  parseHelloAck, parseHelloChallenge, parseLiveViewAck, parseLiveViewBusy, parseResult, ROOM_LINK_END_REASONS,
  ROOM_LINK_PROTOCOL, roomLinkHelloProofText, roomLinkTicketClaims,
  type RoomLinkEndReason, type RoomLinkInbound, type RoomLinkInteraction, type RoomLinkLiveViewAck,
  type RoomLinkLiveViewDuration, type RoomLinkLiveViewHolder, type RoomLinkOutbound,
} from './roomLinkCodec';
import { WebRtcTurnTransport } from './roomLinkWebRtc';

/**
 * The console's session link to the controller of the open room (`room-link/1`, plan
 * 2026-10-21-plan-controller-session-link, S28).
 *
 * In plain terms: the console opens one phone line to the controller when a room is opened and keeps it. A
 * press is spoken down the line and answered on it; the controller's state echo comes back on it; the Live view
 * is "turn on frames" on it. Nuance Cloud still gets the record of everything, so a console without a line reads
 * the same room a moment later. When there is no line (opening, controller offline, relay unreachable), every
 * command takes the cloud queue exactly as before (D12): this object's `state()` is what the console's one
 * command funnel asks, once, at send time.
 *
 * One `RoomLink` per (console tab, room's controller). Inside, at most two links: the one in use and, during a
 * make-before-break reopen before the ticket expires (D3), the next one. A link that dies reopens with backoff
 * (D1). The messages are transport-independent (D20); the transport is chosen by the device's published
 * capability, and this release has one: `room-link/1` over `webrtc-turn`.
 */

export type RoomLinkState = 'closed' | 'opening' | 'open';

/** The platform callable the link's grant goes through: the console's `cloudCall`. */
export type RoomLinkCall = <T>(name: string, params: Record<string, unknown>) => Promise<T>;

/** What the console's command funnel hands back for one command, on either path (S16: `path`). */
export interface CloudControllerReceipt<T> {
  deviceId: string;
  cmdId: string;
  class: 'discrete' | 'gesture';
  coalesced: boolean;
  status: 'pending' | 'done' | 'announced';
  authoredRevision?: number;
  /**
   * How much longer the cloud will carry this command, in milliseconds — a DURATION, measured and
   * clamped by the relay against server time.
   *
   * A duration and deliberately not an absolute instant. An absolute would be a server-epoch value this
   * console could only use by comparing it against its own `Date.now()`, and a browser five minutes SLOW
   * would then read every deadline as five and a half minutes away — the unbounded overlay this bound
   * exists to fix, silently restored on exactly the machines that cannot tell. Five minutes fast and
   * every edit would flash and revert instantly. Added to the local clock it is one clock throughout,
   * and there is no cross-clock comparison anywhere in the console.
   *
   * A link command carries no deadline (it executes on arrival, D6), so a link receipt has none and the
   * console's unreported-command fallback bounds its overlay.
   */
  expiresInMs?: number;
  result?: T;
  /** Which path answered: the open link, or the cloud queue. */
  path: 'link' | 'queue';
}

export interface RoomLinkCommand {
  name: string;
  params: Record<string, unknown>;
  coalesceKey: string | null;
  interaction: RoomLinkInteraction | null;
  /** The command's class in the controller's own catalog (the controller decides it again; this is the receipt's). */
  commandClass: 'discrete' | 'gesture';
}

/** S19: the relay's `activity` and `lastCommand` shapes, as the controller fans them out to every open link. */
export interface RoomLinkCollab { activity?: Record<string, unknown>; lastCommand?: Record<string, unknown> }

/** The Live view's half of the link (D14, S21), as the console's live view store consumes it. */
export interface RoomLinkLiveViewHandlers {
  onAck: (ack: RoomLinkLiveViewAck) => void;
  onBusy: (holder: RoomLinkLiveViewHolder) => void;
  onEnd: (reason: OutputViewEndReason, byDisplayName?: string) => void;
  onMap: (map: WireMap | null) => void;
  onMapState: (state: OutputViewMapState | null) => void;
  onWire: (report: OutputViewWireReport) => void;
  onFrame: (frame: WireFrame, clockOffsetUs: bigint) => void;
}

/** A link's open, in ms from the moment it started (S29); a mark the link never reached is null. */
export interface RoomLinkPhases {
  linkId: string | null;
  grantMs: number | null;
  answerReceivedMs: number | null;
  answerVia: 'listener' | 'read' | null;
  connectedMs: number | null;
  channelOpenMs: number | null;
  helloAckMs: number | null;
  snapshotDoneMs: number | null;
  /** From the start to the moment the link can carry a command: its helloAck. */
  linkOpenMs: number | null;
}

/** The marks a transport reports while it opens. */
export type RoomLinkTransportPhase = 'grant' | 'answerReceived' | 'connected' | 'channelOpen';

export interface RoomLinkTransportEvents {
  /** The transport can carry the hello. */
  onReady: () => void;
  onMessage: (message: RoomLinkInbound) => void;
  onFrame: (data: unknown) => void;
  onPhase: (mark: RoomLinkTransportPhase, answerVia?: 'listener' | 'read') => void;
  /** The transport ended under the link (never called for the link's own `close`). */
  onClosed: (reason: RoomLinkEndReason) => void;
}

export interface RoomLinkTransportGrant { linkId: string; ticket: string }

/** How messages reach the controller (D20). Implementations: `WebRtcTurnTransport`. */
export interface RoomLinkTransport {
  connect: (clientPublicKey: string) => Promise<RoomLinkTransportGrant>;
  send: (message: RoomLinkOutbound) => void;
  close: () => void;
}

export interface RoomLinkOptions {
  db: Firestore;
  restDb: RestFirestore;
  call: RoomLinkCall;
  deviceId: string;
  venueId: string;
  roomId: string;
  /** The device document's `controllerRoomLink` (S3): which protocol, and so which transport, the controller offers (D20). */
  capability: string;
  /** The client the link is opened as (S6, S28 amended 2026-10-21): 'console' when absent; the gateway's MCP door passes 'mcp'. */
  client?: 'console' | 'wireview' | 'mcp';
  peerFactory: () => RTCPeerConnection;
}

type TransportFactory = (options: RoomLinkOptions, events: RoomLinkTransportEvents) => RoomLinkTransport;

/**
 * D20: the published capability names the transport. This release ships one: the Pi / Mac mini controller's
 * `room-link/1` over WebRTC data channels through the TURN relay.
 */
const TRANSPORTS: Readonly<Record<string, TransportFactory>> = {
  [ROOM_LINK_PROTOCOL]: (options, events) => new WebRtcTurnTransport(options, events),
};

/** Whether a controller document offers a link this console can open. */
export function roomLinkOffered(deviceDoc: Record<string, unknown> | undefined): boolean {
  const capability = deviceDoc?.controllerRoomLink;
  return typeof capability === 'string' && Object.prototype.hasOwnProperty.call(TRANSPORTS, capability);
}

/** The relay's result budget (`waitTimeoutMs`), so a slow controller reads the same on both paths (D12). */
export const ROOM_LINK_RESULT_WAIT_MS = 8_000;
/** After a result timeout the console pings; no pong this soon and the link is dead (D12). */
const PONG_WAIT_MS = 2_000;
/** A link with no helloAck this long after it started is abandoned (S13: hello within 30 s of admission). */
const CONNECT_TIMEOUT_MS = 30_000;
/** D1: reopen after 2 s, doubling, capped at 60 s, reset by a helloAck. */
const REOPEN_FIRST_MS = 2_000;
const REOPEN_MAX_MS = 60_000;
/** D3: the next link opens this long before the current ticket (and its TURN credential) expires. */
const RENEW_LEAD_MS = 600_000;
/** S17: the largest intent params a link sample may carry. */
export const ROOM_LINK_INTENT_MAX_BYTES = 4_096;

function nowUs(): bigint { return BigInt(Math.round((performance.timeOrigin + performance.now()) * 1000)); }

function base64(bytes: Uint8Array): string {
  let raw = '';
  for (const byte of bytes) raw += String.fromCharCode(byte);
  return btoa(raw);
}

/** A failure shaped like the relay callable's, so `relayVerdict` and the plain-language errors read both paths alike. */
function relayError(code: 'failed-precondition' | 'deadline-exceeded', message: string, details: Record<string, unknown>,
  linkLost = false): Error {
  return Object.assign(new Error(message), { code: `functions/${code}`, details, ...(linkLost ? { linkLost: true } : {}) });
}

/** A command the link took and could not answer because the link ended under it (D12). */
export function isRoomLinkLost(error: unknown): boolean {
  return (error as { linkLost?: unknown } | null)?.linkLost === true;
}

interface Outstanding {
  command: RoomLinkCommand;
  cmdId: string;
  resolve: (receipt: CloudControllerReceipt<unknown>) => void;
  reject: (error: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface SessionHost {
  deviceId: string;
  ready: (session: LinkSession) => void;
  ended: (session: LinkSession, reason: RoomLinkEndReason) => void;
  message: (session: LinkSession, message: RoomLinkInbound) => void;
  frame: (session: LinkSession, data: unknown) => void;
  drained: (session: LinkSession) => void;
}

/** One link: its transport, its handshake, its liveness and its outstanding commands. */
class LinkSession {
  state: 'opening' | 'open' | 'ended' = 'opening';
  linkId = '';
  clockOffsetUs = 0n;
  lifetimeMs = 0;
  readonly phases: RoomLinkPhases = { linkId: null, grantMs: null, answerReceivedMs: null, answerVia: null, connectedMs: null,
    channelOpenMs: null, helloAckMs: null, snapshotDoneMs: null, linkOpenMs: null };
  private readonly started = performance.now();
  private readonly transport: RoomLinkTransport;
  private readonly outstanding = new Map<string, Outstanding>();
  private key?: OutputViewKey;
  private ticket = '';
  private nonce = '';
  private seq = 0;
  private pingSeq = 0;
  private t1 = 0n;
  private connectTimer?: ReturnType<typeof setTimeout>;
  private pongTimer?: ReturnType<typeof setTimeout>;
  private phasesLogged = false;

  constructor(factory: TransportFactory, options: RoomLinkOptions, private readonly host: SessionHost) {
    this.transport = factory(options, {
      onReady: () => this.hello(),
      onMessage: (message) => this.receive(message),
      onFrame: (data) => { if (this.state === 'open') this.host.frame(this, data); },
      onPhase: (mark, via) => {
        this.mark(`${mark}Ms` as 'grantMs' | 'answerReceivedMs' | 'connectedMs' | 'channelOpenMs');
        if (via) this.phases.answerVia = via;
      },
      onClosed: (reason) => this.end(reason, false),
    });
  }

  async start(): Promise<void> {
    this.connectTimer = setTimeout(() => this.end('connect_timeout', true), CONNECT_TIMEOUT_MS);
    try {
      this.key = await createOutputViewKey();
      if (this.isEnded()) return;
      const grant = await this.transport.connect(this.key.publicKey);
      if (this.isEnded()) return;
      const claims = roomLinkTicketClaims(grant.ticket);
      this.linkId = grant.linkId;
      this.phases.linkId = grant.linkId;
      this.ticket = grant.ticket;
      this.nonce = claims.nonce;
      this.lifetimeMs = (claims.expiresAt - claims.issuedAt) * 1000;
    } catch {
      this.end('transport_closed', true);
    }
  }

  /** Read through a method so a check after an `await` is not narrowed away by the one before it. */
  private isEnded(): boolean { return this.state === 'ended'; }

  private mark(name: Exclude<keyof RoomLinkPhases, 'linkId' | 'answerVia'>): void {
    if (this.phases[name] === null) this.phases[name] = Math.round(performance.now() - this.started);
  }

  /** S29: one line per link, when its first snapshot lands or when it ends first. */
  logPhases(): void {
    if (this.phasesLogged || !this.linkId) return;
    this.phasesLogged = true;
    console.info(`${ROOM_LINK_PHASES_PREFIX}${JSON.stringify(this.phases)}`);
  }

  snapshotDone(): void {
    this.mark('snapshotDoneMs');
    this.logPhases();
  }

  send(message: RoomLinkOutbound): void {
    if (this.state !== 'ended') this.transport.send(message);
  }

  private hello(): void {
    if (this.state !== 'opening' || !this.linkId) return;
    this.t1 = nowUs();
    this.send({ type: 'hello', version: ROOM_LINK_PROTOCOL, linkId: this.linkId, ticket: this.ticket, clientTimeUs: String(this.t1) });
  }

  private async proveHello(message: RoomLinkInbound): Promise<void> {
    const challenge = parseHelloChallenge(message, this.linkId, this.t1);
    if (!challenge || !this.key) { this.end('transport_closed', true); return; }
    const t4 = nowUs();
    const offset = ((challenge.deviceReceiveTimeUs - this.t1) + (challenge.deviceSendTimeUs - t4)) / 2n;
    const rtt = (t4 - this.t1) - (challenge.deviceSendTimeUs - challenge.deviceReceiveTimeUs);
    const clampedRtt = rtt < 0n ? 0n : rtt;
    const text = roomLinkHelloProofText({ linkId: this.linkId, nonce: this.nonce, deviceChallenge: challenge.deviceChallenge,
      clientTimeUs: this.t1, deviceReceiveTimeUs: challenge.deviceReceiveTimeUs, deviceSendTimeUs: challenge.deviceSendTimeUs,
      clientReceiveTimeUs: t4, clockOffsetUs: offset, rttUs: clampedRtt });
    try {
      const signature = new Uint8Array(await crypto.subtle.sign('Ed25519', this.key.privateKey, new TextEncoder().encode(text)));
      this.send({ type: 'helloProof', version: ROOM_LINK_PROTOCOL, linkId: this.linkId, signature: base64(signature),
        clientReceiveTimeUs: String(t4), clockOffsetUs: String(offset), rttUs: String(clampedRtt) });
    } catch { this.end('transport_closed', true); }
  }

  private receive(message: RoomLinkInbound): void {
    if (this.state === 'ended') return;
    if (message.type === 'end') {
      const reason = typeof message.reason === 'string' && ROOM_LINK_END_REASONS.has(message.reason)
        ? message.reason as RoomLinkEndReason : 'transport_closed';
      this.end(reason, false);
      return;
    }
    if (this.state === 'opening') {
      if (message.type === 'helloChallenge') { void this.proveHello(message); return; }
      if (message.type === 'helloAck') {
        const ack = parseHelloAck(message, this.linkId);
        if (!ack) { this.end('transport_closed', true); return; }
        this.clockOffsetUs = ack.clockOffsetUs;
        this.state = 'open';
        if (this.connectTimer) clearTimeout(this.connectTimer);
        this.connectTimer = undefined;
        this.mark('helloAckMs');
        this.mark('linkOpenMs');
        this.host.ready(this);
      }
      return;
    }
    // The controller's liveness ping is answered from here, never from a console timer, so a throttled
    // background tab cannot let its link die (D1).
    if (message.type === 'ping') {
      if (Number.isSafeInteger(message.seq)) this.send({ type: 'pong', seq: message.seq as number });
      return;
    }
    if (message.type === 'pong') {
      if (this.pongTimer) clearTimeout(this.pongTimer);
      this.pongTimer = undefined;
      return;
    }
    if (message.type === 'result') { this.result(message); return; }
    this.host.message(this, message);
  }

  /** S15: the command goes out numbered `<linkId>.<seq>` and is answered by S16 or by the relay's budget. */
  command(command: RoomLinkCommand): Promise<CloudControllerReceipt<unknown>> {
    const cmdId = `${this.linkId}.${++this.seq}`;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => this.resultTimedOut(cmdId), ROOM_LINK_RESULT_WAIT_MS);
      this.outstanding.set(cmdId, { command, cmdId, resolve, reject, timer });
      this.send({ type: 'command', cmdId, envelope: { name: command.name, paramsJson: JSON.stringify(command.params) },
        coalesceKey: command.coalesceKey, interaction: command.interaction });
    });
  }

  outstandingCount(): number { return this.outstanding.size; }

  private settle(cmdId: string): Outstanding | undefined {
    const entry = this.outstanding.get(cmdId);
    if (!entry) return undefined;
    this.outstanding.delete(cmdId);
    clearTimeout(entry.timer);
    if (!this.outstanding.size) this.host.drained(this);
    return entry;
  }

  private result(message: RoomLinkInbound): void {
    const result = parseResult(message);
    if (!result) { this.end('transport_closed', true); return; }
    const entry = this.settle(result.cmdId);
    if (!entry) return;
    if (result.status === 'rejected') {
      entry.reject(relayError('failed-precondition', result.error?.message || 'The controller rejected the command.',
        { controllerCode: result.error?.code || 'rejected', cmdId: result.cmdId }));
      return;
    }
    let decoded: unknown;
    try {
      decoded = result.resultJson === undefined ? undefined : JSON.parse(result.resultJson);
    } catch {
      entry.reject(relayError('failed-precondition', 'The controller returned an unreadable command result.', { cmdId: result.cmdId }));
      return;
    }
    entry.resolve({
      deviceId: this.host.deviceId, cmdId: result.cmdId, class: entry.command.commandClass, coalesced: false, status: 'done',
      ...(result.authoredRevision !== undefined ? { authoredRevision: result.authoredRevision } : {}),
      ...(decoded !== undefined ? { result: decoded } : {}),
      path: 'link',
    });
  }

  /**
   * The relay's budget ran out (D12): the caller hears what a relay timeout says, and the link is asked whether it
   * is still alive. No pong within 2 s and it is dead.
   */
  private resultTimedOut(cmdId: string): void {
    const entry = this.settle(cmdId);
    if (!entry) return;
    entry.reject(relayError('deadline-exceeded', 'Timed out waiting for the controller to acknowledge the command.', { cmdId }));
    if (this.state !== 'open' || this.pongTimer) return;
    this.send({ type: 'ping', seq: ++this.pingSeq });
    this.pongTimer = setTimeout(() => { this.pongTimer = undefined; this.end('link_dead', true); }, PONG_WAIT_MS);
  }

  /** End this link. `tell` sends the controller an `end` first, when the console is the side ending it. */
  end(reason: RoomLinkEndReason, tell: boolean): void {
    if (this.state === 'ended') return;
    if (tell) this.send({ type: 'end', reason });
    this.state = 'ended';
    if (this.connectTimer) clearTimeout(this.connectTimer);
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.transport.close();
    this.logPhases();
    for (const entry of [...this.outstanding.values()]) {
      this.outstanding.delete(entry.cmdId);
      clearTimeout(entry.timer);
      entry.reject(relayError('deadline-exceeded', 'The link to the controller ended before it answered.', { cmdId: entry.cmdId }, true));
    }
    this.host.ended(this, reason);
  }
}

/** The one console line per link that carries its open phases (S29); the live runner reads it. */
export const ROOM_LINK_PHASES_PREFIX = '[room-link] phases ';
/** The one console line per command (S29). */
export const ROOM_LINK_COMMAND_PREFIX = '[room-link] command ';
/** The one console line per live view on the link (S29). */
export const ROOM_LINK_LIVE_VIEW_PREFIX = '[room-link] liveView ';

/** The live view on the link, as the manager holds it across a reopen. */
interface LiveViewBinding {
  handlers: RoomLinkLiveViewHandlers;
  duration: RoomLinkLiveViewDuration;
  /** The link the controller is streaming on. */
  session: LinkSession;
  stream: OutputViewStream;
  acknowledged: boolean;
}

export class RoomLink {
  /** Called on every change of `state()`. */
  onState?: (state: RoomLinkState) => void;
  /**
   * S20: one authored node from the controller, on the link session `session` (its linkId). A changed node
   * carries the same revision as its cloud copy; an unchanged node in the join snapshot carries the
   * snapshot's revision, which the cloud copy does not (B118) — so revisions are ordered per session.
   */
  onNode?: (path: string, node: unknown, session: string) => void;
  /** S20: the controller finished the snapshot of what this console subscribed to. */
  onSnapshotDone?: (revision: number, epoch: string) => void;
  /** S19: who is working the room. */
  onCollab?: (collab: RoomLinkCollab) => void;

  private readonly factory: TransportFactory;
  private active?: LinkSession;
  private next?: LinkSession;
  private readonly retiring = new Set<LinkSession>();
  private closed = false;
  private opened = false;
  private lastState: RoomLinkState = 'closed';
  private backoffMs = REOPEN_FIRST_MS;
  private reopenTimer?: ReturnType<typeof setTimeout>;
  private renewTimer?: ReturnType<typeof setTimeout>;
  private subscription?: { domains: string[]; fixtures: boolean };
  private liveView?: LiveViewBinding;

  constructor(private readonly options: RoomLinkOptions) {
    const factory = TRANSPORTS[options.capability];
    if (!factory) throw new Error(`room link: no transport for capability ${options.capability}`);
    this.factory = factory;
  }

  /** Start the first link. A link that dies reopens on its own until `close`. */
  open(): void {
    if (this.closed || this.opened) return;
    this.opened = true;
    this.begin();
  }

  state(): RoomLinkState {
    if (this.active?.state === 'open') return 'open';
    if (this.active?.state === 'opening') return 'opening';
    return 'closed';
  }

  /** The open phases of the link in use (S29). */
  phases(): RoomLinkPhases | undefined {
    return this.active ? { ...this.active.phases } : undefined;
  }

  /**
   * S15/S16: one command on the open link. Only call while `state()` is `open`: the path is chosen once, at
   * send time, by the caller (D12), and a command handed here is never also queued.
   */
  send<T>(command: RoomLinkCommand): Promise<CloudControllerReceipt<T>> {
    const session = this.active;
    if (!session || session.state !== 'open') return Promise.reject(new Error('room link: not open'));
    return session.command(command) as Promise<CloudControllerReceipt<T>>;
  }

  /** S17: one keep-last drag sample. False when it cannot go (no open link, or params over 4,096 bytes). */
  intent(sample: { rigId: string; controlKey: string; command: string; paramsJson: string }): boolean {
    const session = this.active;
    if (!session || session.state !== 'open') return false;
    if (new TextEncoder().encode(sample.paramsJson).length > ROOM_LINK_INTENT_MAX_BYTES) return false;
    session.send({ type: 'intent', ...sample });
    return true;
  }

  /** S18: who is working the room, for the controller to record and fan out. */
  announce(activity: { command: string; coalesceKey: string; interaction: RoomLinkInteraction; paramsJson: string | null }): boolean {
    const session = this.active;
    if (!session || session.state !== 'open') return false;
    session.send({ type: 'announce', ...activity });
    return true;
  }

  /** S20: which domains of the room this console follows; resent to every link that opens. */
  subscribe(domains: string[], fixtures: boolean): void {
    const next = { domains: [...domains], fixtures };
    if (this.subscription && JSON.stringify(this.subscription) === JSON.stringify(next)) return;
    this.subscription = next;
    if (this.active?.state === 'open') this.active.send({ type: 'subscribe', ...next });
  }

  /** D14: turn on frames on the open link. The controller answers liveViewAck, liveViewBusy or liveViewEnd. */
  liveViewStart(duration: RoomLinkLiveViewDuration, handlers: RoomLinkLiveViewHandlers): boolean {
    return this.requestLiveView('start', duration, handlers);
  }

  /** D14: end the holder's view and start this one (B77's Take over, one message). */
  liveViewTakeOver(duration: RoomLinkLiveViewDuration, handlers: RoomLinkLiveViewHandlers): boolean {
    return this.requestLiveView('takeOver', duration, handlers);
  }

  /** B114: switch the running view between two minutes and indefinite; answered by liveViewAck. */
  liveViewSetDuration(duration: RoomLinkLiveViewDuration): boolean {
    const view = this.liveView;
    if (!view || view.session.state !== 'open') return false;
    view.duration = duration;
    view.session.send({ type: 'liveView', action: 'setDuration', duration });
    return true;
  }

  /** The viewer's Stop: the controller stops the frames; the link stays. */
  liveViewStop(): void {
    const view = this.liveView;
    if (!view) return;
    this.liveView = undefined;
    view.stream.stop();
    view.session.send({ type: 'liveView', action: 'stop' });
  }

  /** Close every link and stop reopening (room change, sign-out, page hide). */
  close(reason: RoomLinkEndReason = 'client_closed'): void {
    if (this.closed) return;
    this.closed = true;
    if (this.reopenTimer) clearTimeout(this.reopenTimer);
    if (this.renewTimer) clearTimeout(this.renewTimer);
    const sessions = [this.active, this.next, ...this.retiring].filter((session): session is LinkSession => Boolean(session));
    this.active = undefined;
    this.next = undefined;
    this.retiring.clear();
    for (const session of sessions) session.end(reason, true);
    this.endLiveView('client_end');
    this.emitState();
  }

  private requestLiveView(action: 'start' | 'takeOver', duration: RoomLinkLiveViewDuration, handlers: RoomLinkLiveViewHandlers): boolean {
    const session = this.active;
    if (!session || session.state !== 'open') return false;
    this.liveView?.stream.stop();
    this.liveView = { handlers, duration, session, stream: this.liveViewStream(handlers, session), acknowledged: false };
    session.send({ type: 'liveView', action, duration });
    return true;
  }

  private liveViewStream(handlers: RoomLinkLiveViewHandlers, session: LinkSession): OutputViewStream {
    return new OutputViewStream({
      onFrame: handlers.onFrame, onMap: handlers.onMap, onWire: handlers.onWire, onMapState: handlers.onMapState,
      sendReady: (mapDigest) => this.liveView?.session.send({ type: 'ready', mapDigest }),
    }, () => this.liveView?.session.clockOffsetUs ?? session.clockOffsetUs);
  }

  private endLiveView(reason: OutputViewEndReason, byDisplayName?: string): void {
    const view = this.liveView;
    if (!view) return;
    this.liveView = undefined;
    view.stream.stop();
    view.handlers.onEnd(reason, byDisplayName);
  }

  private begin(): void {
    if (this.closed) return;
    const session = this.newSession();
    this.active = session;
    this.emitState();
    void session.start();
  }

  private newSession(): LinkSession {
    return new LinkSession(this.factory, this.options, {
      deviceId: this.options.deviceId,
      ready: (session) => this.sessionReady(session),
      ended: (session, reason) => this.sessionEnded(session, reason),
      message: (session, message) => this.sessionMessage(session, message),
      frame: (session, data) => { if (this.liveView?.session === session) void this.liveView.stream.frame(data); },
      drained: (session) => { if (this.retiring.has(session)) session.end('client_closed', true); },
    });
  }

  private sessionReady(session: LinkSession): void {
    this.backoffMs = REOPEN_FIRST_MS;
    if (session === this.next) {
      // Make-before-break (D3): new commands go on the new link from its helloAck. The old one closes once its
      // outstanding results are in (each is bounded by the result budget), and a running view moves.
      const previous = this.active;
      this.active = session;
      this.next = undefined;
      if (previous) {
        if (previous.outstandingCount()) this.retiring.add(previous);
        else previous.end('client_closed', true);
      }
      this.moveLiveView(session);
    }
    if (this.subscription) session.send({ type: 'subscribe', ...this.subscription });
    else session.logPhases();
    this.scheduleRenewal(session);
    this.emitState();
  }

  /** The same viewer moving between its own links is a move, not a takeover (D14). */
  private moveLiveView(session: LinkSession): void {
    const view = this.liveView;
    if (!view || view.session === session) return;
    view.session = session;
    view.stream.restart();
    session.send({ type: 'liveView', action: 'start', duration: view.duration });
  }

  private scheduleRenewal(session: LinkSession): void {
    if (this.renewTimer) clearTimeout(this.renewTimer);
    this.renewTimer = undefined;
    // A ticket shorter than the lead cannot be renewed ahead of time; that link ends at expiry and reopens.
    if (session.lifetimeMs <= RENEW_LEAD_MS) return;
    this.renewTimer = setTimeout(() => this.renew(session), session.lifetimeMs - RENEW_LEAD_MS);
  }

  private renew(session: LinkSession): void {
    this.renewTimer = undefined;
    if (this.closed || this.active !== session || this.next) return;
    const next = this.newSession();
    this.next = next;
    void next.start();
  }

  private sessionEnded(session: LinkSession, reason: RoomLinkEndReason): void {
    this.retiring.delete(session);
    if (this.closed) return;
    if (session === this.next) {
      this.next = undefined;
      // The link in use is still good until its own expiry; try the next one again on the reopen clock.
      const current = this.active;
      if (current) this.renewTimer = setTimeout(() => this.renew(current), this.takeBackoff());
      return;
    }
    if (session !== this.active) return;
    this.active = undefined;
    if (this.liveView?.session === session) this.endLiveView(liveViewEndReason(reason));
    if (this.renewTimer) clearTimeout(this.renewTimer);
    this.renewTimer = undefined;
    if (this.next) {
      // The replacement is already opening: it becomes the link in use, and no second one is started.
      this.active = this.next;
      this.next = undefined;
      this.emitState();
      return;
    }
    this.emitState();
    // D8: a role or membership change ends the link so a fresh ticket carries the new permission set: at once.
    const delay = reason === 'role_changed' || reason === 'membership_revoked' ? 0 : this.takeBackoff();
    this.reopenTimer = setTimeout(() => { this.reopenTimer = undefined; this.begin(); }, delay);
  }

  private takeBackoff(): number {
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 2, REOPEN_MAX_MS);
    return delay;
  }

  private sessionMessage(session: LinkSession, message: RoomLinkInbound): void {
    const view = this.liveView;
    switch (message.type) {
      case 'node':
        if (session === this.active && typeof message.path === 'string') this.onNode?.(message.path, message.node, session.linkId);
        return;
      case 'snapshotDone':
        session.snapshotDone();
        if (session === this.active && Number.isSafeInteger(message.revision) && typeof message.epoch === 'string') {
          this.onSnapshotDone?.(message.revision as number, message.epoch);
        }
        return;
      case 'collab':
        if (session === this.active) {
          this.onCollab?.({
            ...(typeof message.activity === 'object' && message.activity !== null ? { activity: message.activity as Record<string, unknown> } : {}),
            ...(typeof message.lastCommand === 'object' && message.lastCommand !== null ? { lastCommand: message.lastCommand as Record<string, unknown> } : {}),
          });
        }
        return;
      case 'liveViewAck': {
        const ack = parseLiveViewAck(message);
        if (!view || view.session !== session || !ack) return;
        view.duration = ack.duration;
        if (!view.acknowledged) { view.acknowledged = true; view.stream.begin(); }
        view.handlers.onAck(ack);
        return;
      }
      case 'liveViewBusy': {
        const busy = parseLiveViewBusy(message);
        if (!view || view.session !== session || !busy) return;
        this.liveView = undefined;
        view.stream.stop();
        view.handlers.onBusy(busy.holder);
        return;
      }
      case 'liveViewEnd':
        if (!view || view.session !== session) return;
        this.endLiveView(liveViewEndReason(typeof message.reason === 'string' ? message.reason : ''),
          typeof message.byDisplayName === 'string' && message.byDisplayName ? message.byDisplayName : undefined);
        return;
      default:
        if (view && view.session === session) view.stream.control(message);
    }
  }

  private emitState(): void {
    const state = this.state();
    if (state === this.lastState) return;
    this.lastState = state;
    this.onState?.(state);
  }
}

/** The live view's words for why it ended (the output-view vocabulary; `useOutputView.outputViewReason`). */
const OUTPUT_VIEW_END_REASONS: ReadonlySet<string> = new Set<OutputViewEndReason>([
  'client_end', 'connect_timeout', 'device_revoked', 'lease_expired', 'membership_revoked', 'operator_end', 'session_ceiling',
  'link_dead', 'superseded', 'taken_over', 'time_limit', 'transport_closed', 'transport_policy',
]);

/** A link or live view end reason in the live view's vocabulary; anything it has no words for is "Connection ended". */
function liveViewEndReason(reason: string): OutputViewEndReason {
  if (reason === 'client_closed') return 'client_end';
  return OUTPUT_VIEW_END_REASONS.has(reason) ? reason as OutputViewEndReason : 'transport_closed';
}

/**
 * The per-command measures (S29): from send to the result, to the first link node at or past the result's
 * revision (`echoMs`), and to the cloud listener delivering it (`cloudEchoMs`), with whether the reducer read
 * that cloud copy as a duplicate of what the link had already delivered. Measured, never gated (Rule 4).
 */
export class RoomLinkMeter {
  private readonly arrivals: Array<{ source: 'link' | 'cloud'; revision: number; at: number; duplicate: boolean }> = [];
  private readonly open = new Map<string, { line: Record<string, unknown>; sentAt: number; revision?: number;
    timer: ReturnType<typeof setTimeout> }>();

  /** How long a command's line waits for its echoes before it is written with what arrived. */
  static readonly ECHO_WAIT_MS = 15_000;

  /** A command was answered (or refused); `sentAt` is `performance.now()` at the funnel. */
  commandAnswered(entry: { cmdId: string; name: string; path: 'link' | 'queue'; sentAt: number; revision?: number }): void {
    const line = { cmdId: entry.cmdId, name: entry.name, path: entry.path,
      resultMs: Math.round(performance.now() - entry.sentAt), echoMs: null, cloudEchoMs: null, cloudEchoDuplicate: null } as Record<string, unknown>;
    if (entry.revision === undefined) { this.write(line); return; }
    const timer = setTimeout(() => this.finish(entry.cmdId), RoomLinkMeter.ECHO_WAIT_MS);
    this.open.set(entry.cmdId, { line, sentAt: entry.sentAt, revision: entry.revision, timer });
    for (const arrival of this.arrivals) this.apply(entry.cmdId, arrival);
  }

  /** One authored node reached the reducer, from the link or from the cloud listener. */
  nodeArrived(source: 'link' | 'cloud', revision: unknown, duplicate: boolean): void {
    if (!Number.isSafeInteger(revision)) return;
    const arrival = { source, revision: revision as number, at: performance.now(), duplicate };
    this.arrivals.push(arrival);
    if (this.arrivals.length > 256) this.arrivals.splice(0, this.arrivals.length - 256);
    for (const cmdId of [...this.open.keys()]) this.apply(cmdId, arrival);
  }

  stop(): void {
    for (const cmdId of [...this.open.keys()]) this.finish(cmdId);
  }

  private apply(cmdId: string, arrival: { source: 'link' | 'cloud'; revision: number; at: number; duplicate: boolean }): void {
    const entry = this.open.get(cmdId);
    if (!entry || entry.revision === undefined || arrival.revision < entry.revision || arrival.at < entry.sentAt) return;
    const key = arrival.source === 'link' ? 'echoMs' : 'cloudEchoMs';
    if (entry.line[key] !== null) return;
    entry.line[key] = Math.round(arrival.at - entry.sentAt);
    if (arrival.source === 'cloud') entry.line.cloudEchoDuplicate = arrival.duplicate;
    const linkSeen = entry.line.path === 'queue' || entry.line.echoMs !== null;
    if (linkSeen && entry.line.cloudEchoMs !== null) this.finish(cmdId);
  }

  private finish(cmdId: string): void {
    const entry = this.open.get(cmdId);
    if (!entry) return;
    this.open.delete(cmdId);
    clearTimeout(entry.timer);
    this.write(entry.line);
  }

  private write(line: Record<string, unknown>): void {
    console.info(`${ROOM_LINK_COMMAND_PREFIX}${JSON.stringify(line)}`);
  }
}
